import { useState } from 'react';
import type { StepProps } from '../../OBWWizard';
import { ENV } from '../../../amplify-config';

// ADR-076 Option 2 — staff fork of pa-submit. The subscriber step posts to the
// T1 /portal/pow/submit (subscriber JWT); here there is no subscriber session for
// the target, so this posts to the T0 /portal/pow/provision with the admin-minted
// POW launch token (data.powToken) as the auth + identity carrier. The brief is
// keyed server-side to the PICKED subscriber.
//
// ⛔ NO BOT CHECK HERE, ON PURPOSE (Jeff, 2 October 2026 — ADR-029 §5a amendment):
// "a bot check belongs at the door, not inside the house." The staff member is signed in to
// the ops console (itself bot-checked at staff sign-in) and the admin-minted launch token is
// the identity carrier. A second check added a failure point and no protection. Do not re-add
// a widget to this step.

interface SubmittedData {
  powToken?: string;
  customerNumber?: string;
  email?: string;
  businessName?: string;
  websiteUrl?: string;
  jurisdiction?: 'AU' | 'NZ' | 'US';
  registrationNumber?: string;
  descriptionOfPractice?: string;
  // ADR-077 A1.4 — set by the pick-step fork's "Start fresh"; supersedes the
  // picked subscriber's in-progress Brief at provision.
  supersedeApplicationId?: string;
}

export function PartnerSubmitStep({ data, onNext }: StepProps) {
  const d = data as SubmittedData;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  async function handleSubmit() {
    if (!d.powToken || !d.businessName || !d.websiteUrl || !d.jurisdiction || !d.registrationNumber || !d.descriptionOfPractice) {
      setError({ code: 'INCOMPLETE', message: 'Some application details are missing. Go back to confirm.' });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${ENV.PORTAL_API_BASE_URL}/portal/pow/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preFillToken: d.powToken,
          businessName: d.businessName,
          websiteUrl: d.websiteUrl,
          registrationNumber: d.registrationNumber,
          jurisdiction: d.jurisdiction,
          descriptionOfPractice: d.descriptionOfPractice,
          ...(d.supersedeApplicationId ? { supersedeApplicationId: d.supersedeApplicationId } : {}),
        }),
      });
      const bodyJson = (await res.json().catch(() => ({}))) as { applicationId?: string; submittedAt?: string; status?: string; error?: { code?: string; message?: string } };
      if (!res.ok) {
        const code = bodyJson.error?.code ?? 'SUBMIT_FAILED';
        const message = code === 'APPLICATION_IN_PROGRESS'
          ? 'This subscriber already has an application in progress.'
          : bodyJson.error?.message ?? 'Could not submit the application. Please try again.';
        setError({ code, message });
        return;
      }
      onNext({ applicationId: bodyJson.applicationId, submittedAt: bodyJson.submittedAt, status: bodyJson.status });
    } catch {
      setError({ code: 'NETWORK', message: 'Could not submit. Check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Submit the application</h2>
      <p className="obw-subtitle">
        Review the summary below. On submit we run the suitability assessment for this subscriber;
        the outcome lands in the Partners queue and the subscriber is notified by email.
      </p>

      <div className="obw-auth-box">
        <p className="obw-form-section" style={{ marginTop: 0 }}>Application summary</p>
        <dl style={{ display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: '8px', columnGap: '16px', margin: 0 }}>
          <dt style={{ color: 'var(--text-dim)' }}>Customer number</dt><dd style={{ margin: 0, color: 'var(--text)' }}>{d.customerNumber}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Email</dt><dd style={{ margin: 0, color: 'var(--text)' }}>{d.email}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Business name</dt><dd style={{ margin: 0, color: 'var(--text)' }}>{d.businessName}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Website</dt><dd style={{ margin: 0, color: 'var(--text)' }}>{d.websiteUrl}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Jurisdiction</dt><dd style={{ margin: 0, color: 'var(--text)' }}>{d.jurisdiction}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Registration</dt><dd style={{ margin: 0, color: 'var(--text)' }}>{d.registrationNumber}</dd>
          <dt style={{ color: 'var(--text-dim)' }}>Description</dt><dd style={{ margin: 0, color: 'var(--text)' }}>{d.descriptionOfPractice}</dd>
        </dl>
      </div>

      {error && (
        <div className="portal-wizard-error" role="alert" style={{ marginTop: '16px' }}>
          <strong>{error.code}</strong> · {error.message}
        </div>
      )}

      <div className="portal-wizard-nav">
        <button type="button" className="obw-btn-primary" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit application →'}
        </button>
      </div>
    </div>
  );
}
